import React, { useEffect, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { site } from '@panel/commons/helpers';
import { useBreakpoint } from '@panel/commons';
import { useQuery } from '@panel/commons/hooks';
import { select, withQuery } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import Cookies from 'js-cookie';
import { SavePostingSelectors } from '../../ducks/savePosting';
import statusSuccess from '../../../assets/img/statusSuccess.svg';
import { sendEvent, sendPaymentNotification } from '../../utils';
import { Step1Module1Selectors } from '../../ducks/step1Module1';
import { selectEcommerceUser } from '../../ducks/step4Module2';
import { SuccessPublishBanner } from './SuccessPublishBanner';

const { isBrasil } = site;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  & > div {
    white-space: normal;
    margin-top: 25px;
    padding: 0 45px;
    ${withQuery.lg.up()} {
      max-width: 650px;
    }
    ${withQuery.xs.down()} {
      margin-top: ${isBrasil ? '40' : '20'}px;
    }
    & > img {
      width: 100%;
      ${withQuery.lg.down()} {
        width: 75%;
      }
      ${withQuery.xs.down()} {
        display: inline-block;
      }
    }
  }

  ${withQuery.lg.down()} {
    flex-direction: column;
  }
`;

const Title = styled.h2`
  font-size: 4.2rem;
  color: ${select('positive')};
  line-height: 1;
  ${withQuery.lg.down()} {
    font-size: 2rem;
  }
  ${withQuery.lg.up()} {
    padding-top: 60px;
  }
`;

const Text = styled.p`
  margin-top: 20px;
  color: ${select('violet')};
  line-height: 1.3;
  margin-bottom: 80px;
  ${withQuery.lg.down()} {
    margin-bottom: 20px;
  }
`;

const StyledButton = css`
  border-radius: 12px;
  padding: 10px 24px;
  text-decoration: none;
  ${withQuery.lg.up()} {
    margin-right: 24px;
  }
  ${withQuery.lg.down()} {
    display: block;
  }
`;

const Cta1 = styled.a`
  ${StyledButton}
  background: ${getColor('primary')};
  color: ${select('white')};
`;

const Cta2 = styled.a`
  ${StyledButton}
  background: transparent;
  border: 1px solid ${getColor('primary')};
  color: ${select('black')};
  ${withQuery.lg.down()} {
    margin-bottom: 20px;
  }
`;

function StatusSuccess() {
  const [planInfo, setPlanInfo] = useState('');
  const { postingPublished } = useSelector(SavePostingSelectors.getTypePublished);
  const { email } = useSelector(selectEcommerceUser);
  const {
    formValues: { selectedOperationTypeId }
  } = useSelector(Step1Module1Selectors.getStep1Module1State);
  const [reachedBreakPoint] = useBreakpoint(1280);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { t } = useTranslation();
  const {
    state: { fromPreview }
  } = useLocation();

  const titleKey = postingPublished
    ? 'publisher.stepStatus.success.title'
    : 'publisher.stepStatus.success.title.edition';
  const textKey = postingPublished ? 'publisher.stepStatus.success.text' : 'publisher.stepStatus.success.text.edition';

  const getPlanId = useCallback(() => {
    const planCookie = Cookies.get(`selectedPlan${postingId}`);
    if (planCookie) {
      const highlight = planCookie.split('_');
      const planSelect = highlight[2];
      if (planSelect === 'free') {
        setPlanInfo('Gratis');
      } else if (planSelect === 'simple') {
        setPlanInfo('Simple');
      } else if (planSelect === 'highlighted') {
        setPlanInfo('Destacado');
      } else if (planSelect === 'superhighlighted') {
        setPlanInfo('Super destacado');
      }
    }
  }, [postingId]);

  useEffect(() => {
    const sendMail = () => {
      const mailData = {
        userEmailAddress: email,
        postingId
      };
      sendPaymentNotification('ONBOARDING', mailData);
    };

    const dataLayerSuccess = () => {
      let selectedOperationTypeName = '0';
      if (selectedOperationTypeId === '1') {
        selectedOperationTypeName = 'Venta';
      } else if (selectedOperationTypeId === '2') {
        selectedOperationTypeName = 'Alquiler';
      } else if (selectedOperationTypeId === '4') {
        selectedOperationTypeName = 'Alquiler Temporada';
      }

      window.dataLayer.push({
        event: 'trackEvent',
        eventCategory: 'Publicador',
        eventAction: 'Publicacion exitosa',
        eventLabel: `Success-${planInfo}`,
        tipoDeOperacion: selectedOperationTypeName
      });
    };
    if (!planInfo) {
      getPlanId();
    }
    if (selectedOperationTypeId && planInfo) {
      dataLayerSuccess();
      sendMail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperationTypeId, planInfo]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  if (!fromPreview) {
    return (
      <Redirect
        to={{
          pathname: '/avisos'
        }}
      />
    );
  }

  const dataLayerSuccesSeeNotices = () => sendEvent('Publicacion exitosa', 'Ver aviso');
  const dataLayerSuccesMyNotices = () => sendEvent('Publicacion exitosa', 'Ir a Mis avisos');

  return (
    <Container>
      {reachedBreakPoint && (
        <div>
          <Title data-repa='publishStatus-success-title'>{t(titleKey)}</Title>
          <Text>{t(textKey)}</Text>
          <Cta2
            data-repa='publishStatus-success-CTA2'
            href={`../${t('panel.url.property')}/-${postingId}.html`}
            target='_blank'
            rel='noopener noreferrer'
            onClick={dataLayerSuccesSeeNotices}
          >
            {t('publisher.btn.linkToPosting')}
          </Cta2>
          <Cta1 data-repa='publishStatus-success-CTA1' href='/panel/avisos' onClick={dataLayerSuccesMyNotices}>
            {t('publisher.btn.linkToPostings')}
          </Cta1>
        </div>
      )}
      {(!postingPublished && isBrasil) ||
        (!isBrasil && (
          <div>
            <img src={statusSuccess} alt={t(titleKey)} />
          </div>
        ))}
      {reachedBreakPoint && (isBrasil && postingPublished) && <SuccessPublishBanner />}
      {!reachedBreakPoint && (
        <div>
          <Title data-repa='publishStatus-success-title-mobile'>{t(titleKey)}</Title>
          <Text>{t(textKey)}</Text>
          <Cta2
            data-repa='publishStatus-success-CTA2'
            href={`../${t('panel.url.property')}/-${postingId}.html`}
            target='_blank'
            rel='noopener noreferrer'
            onClick={dataLayerSuccesSeeNotices}
          >
            {t('publisher.btn.linkToPosting')}
          </Cta2>
          <Cta1 data-repa='publishStatus-success-CTA1' href='/panel/avisos' onClick={dataLayerSuccesMyNotices}>
            {t('publisher.btn.linkToPostings')}
          </Cta1>
        </div>
      )}
      {!reachedBreakPoint && (isBrasil && postingPublished) && <SuccessPublishBanner />}
    </Container>
  );
}

export { StatusSuccess };
